/** Customer-facing deposit phrase. Blank label → "Deposit"; "bottle" → "Bottle deposit". */
export function depositPhrase(label?: string | null): string {
  const trimmed = (label || '').trim();
  if (!trimmed) return 'Deposit';
  return `${trimmed.charAt(0).toUpperCase()}${trimmed.slice(1)} deposit`;
}

/** Invoice / order line title. The product name stays so two deposits never collapse. */
export function depositLineName(label: string | null | undefined, productName: string): string {
  return `${depositPhrase(label)} — ${productName} (refundable)`;
}

/** Staff lists show the product, not the "Bottle deposit — … (refundable)" wrapper. */
export function depositProductFromLine(productName: string): string {
  return productName.replace(/ \(refundable\)$/i, '').replace(/^.*? deposit — /i, '');
}

/**
 * product_sale_items has no line_type. Old lines start with "Bottle deposit — ".
 * New lines are "{Label} deposit — {product} (refundable)". Refund credit lines
 * start with "Deposit refund — ".
 */
export function isDepositSaleLine(name: string): boolean {
  if (!name) return false;
  if (name.startsWith('Deposit refund — ')) return true;
  return / deposit — /i.test(name) && /\(refundable\)\s*$/i.test(name);
}

export interface ContainerSiteBalance {
  stock_item_id: string;
  quantity: number;
  customer_address_id?: string | null;
}

/**
 * Containers that count at checkout.
 * Pickup (no delivery address) uses only unassigned containers, so a return
 * at the counter does not spend jugs recorded at a site.
 * A chosen delivery address uses that site plus anything still unassigned.
 * A brand-new address uses unassigned only. No address chosen yet uses the
 * shared pool, so customers are not charged twice before they pick a site.
 */
export function containerQtyForCheckout(
  rows: ContainerSiteBalance[],
  opts: { pickup: boolean; addressId: string | null }
): Record<string, number> {
  const map: Record<string, number> = {};
  const chosen = opts.addressId && opts.addressId !== 'new' ? opts.addressId : null;
  for (const row of rows) {
    const addr = row.customer_address_id ?? null;
    let include = true;
    if (opts.pickup) include = addr == null;
    else if (opts.addressId === 'new') include = addr == null;
    else if (chosen) include = addr == null || addr === chosen;
    if (!include || Number(row.quantity) <= 0) continue;
    map[row.stock_item_id] = (map[row.stock_item_id] || 0) + Number(row.quantity);
  }
  return map;
}
